import React from "react";
import { Control, Controller, RegisterOptions } from "react-hook-form";
import { Button } from "@/components/shadcn/button";
import { ConfigProviderApiToggle } from "@/types/api/spotflow/spotflow-config";
import { Loader2 } from "lucide-react";

enum ApiToggleState {
  CONNECTED = "connected",
  DISCONNECTED = "disconnected",
  REQUESTING = "requesting",
  REQUESTING_CONNECT = "requesting_connect",
  REQUESTING_DISCONNECT = "requesting_disconnect",
}

interface ProviderApiToggleFieldProps {
  name: string;
  control: Control<any>;
  config: ConfigProviderApiToggle;
  defaultValue?: ApiToggleState;
  isDisabled?: boolean;
  rules?: RegisterOptions;
  tooltip?: string;
  label?: string;
}

export const ProviderApiToggleField = ({
  name,
  control,
  config,
  defaultValue = ApiToggleState.DISCONNECTED,
  isDisabled = false,
  rules,
}: ProviderApiToggleFieldProps) => {
  const handleToggle = (
    currentValue: ApiToggleState,
    onChange: (value: ApiToggleState) => void
  ) => {
    if (currentValue === ApiToggleState.CONNECTED) {
      onChange(ApiToggleState.REQUESTING_DISCONNECT);
    } else if (currentValue === ApiToggleState.DISCONNECTED) {
      onChange(ApiToggleState.REQUESTING_CONNECT);
    }
  };

  return (
    <div className="flex w-full flex-col gap-1 items-start">
      <Controller
        control={control}
        name={name}
        defaultValue={defaultValue}
        rules={rules}
        disabled={isDisabled}
        render={({ field: { value, onChange }, formState: { errors } }) => {
          const currentState = value as ApiToggleState;
          const isConnected = currentState === ApiToggleState.CONNECTED;
          const isRequestingConnect =
            currentState === ApiToggleState.REQUESTING_CONNECT;
          const isRequestingDisconnect =
            currentState === ApiToggleState.REQUESTING_DISCONNECT;
          const isRequesting = isRequestingConnect || isRequestingDisconnect;

          let currentLabel = config.label;
          if (isConnected) {
            currentLabel = config.toggle_label;
          } else if (isRequestingConnect) {
            currentLabel = "Connecting...";
          } else if (isRequestingDisconnect) {
            currentLabel = "Disconnecting...";
          }

          return (
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => {
                handleToggle(currentState, onChange);
              }}
              disabled={isDisabled || isRequesting}
            >
              <div className="flex items-center gap-2">
                {isRequesting && <Loader2 className="h-4 w-4 animate-spin" />}
                <span className="whitespace-nowrap">{currentLabel}</span>
              </div>
            </Button>
          );
        }}
      />
    </div>
  );
};
